import { PAGE_SIZE_MAX } from "@seagames/contracts";
import { z } from "zod";

/** A URL param that falls back to "not set" when it does not parse. */
type Lenient<T extends z.ZodTypeAny> = z.ZodCatch<z.ZodOptional<T>>;

const lenient = <T extends z.ZodTypeAny>(schema: T): Lenient<T> => schema.optional().catch(undefined);

/** Free text in the URL (`search`, a category): trimmed, 1–100 characters. */
export const searchText = () => z.string().trim().min(1).max(100);

/**
 * The URL search schema every list page shares: `page`, `page_size` (≤
 * PAGE_SIZE_MAX), `search`, `sort` ∈ `sortKeys`, `dir`, plus the page's own
 * filters in `extra`.
 *
 * URL is the state: page, filters and sort live in the search params, so a
 * view is shareable and survives reload / back. EVERY field — `extra`
 * included, wrapped here so a new filter cannot forget it — is
 * `.optional().catch(undefined)`: a hand-edited `?page=abc` falls back
 * instead of rendering an error page.
 *
 * `extra` sits between `search` and `sort`, the order the pages always used,
 * so the params keep their place in the address bar.
 */
export function listSearch<const K extends SortKeys>(sortKeys: K): ReturnType<typeof build<K, {}>>;
export function listSearch<const K extends SortKeys, E extends z.ZodRawShape>(sortKeys: K, extra: E): ReturnType<typeof build<K, E>>;
export function listSearch(sortKeys: SortKeys, extra: z.ZodRawShape = {}): z.AnyZodObject {
	return build(sortKeys, extra);
}

type SortKeys = readonly [string, ...string[]];

// Overloads, not `extra?: E`: with `extra` omitted, TS infers E from
// `validateSearch`'s contextual type (ZodRawShape) and every param turns `any`.
function build<K extends SortKeys, E extends z.ZodRawShape>(sortKeys: K, extra: E) {
	const lenientExtra = Object.fromEntries(Object.entries(extra).map(([key, schema]) => [key, lenient(schema)])) as {
		[P in keyof E]: Lenient<E[P]>;
	};
	return z.object({
		page: lenient(z.coerce.number().int().min(1)),
		page_size: lenient(z.coerce.number().int().min(1).max(PAGE_SIZE_MAX)),
		search: lenient(searchText()),
		...lenientExtra,
		sort: lenient(z.enum(sortKeys)),
		dir: lenient(z.enum(["asc", "desc"])),
	});
}

/**
 * The URL search schema of a KEYSET list (`useListPage({ paging: "keyset" })`):
 * `cursor` (opaque, ≤ 200 like the API's), `prev` (the earlier pages' cursors —
 * a JSON array in the address bar), `limit` (≤ PAGE_SIZE_MAX), plus the
 * page's own params in `extra`, each lenient like `listSearch`'s.
 */
export function keysetSearch<E extends z.ZodRawShape>(extra: E) {
	const lenientExtra = Object.fromEntries(Object.entries(extra).map(([key, schema]) => [key, lenient(schema)])) as {
		[P in keyof E]: Lenient<E[P]>;
	};
	return z.object({
		...lenientExtra,
		limit: lenient(z.coerce.number().int().min(1).max(PAGE_SIZE_MAX)),
		cursor: lenient(z.string().min(1).max(200)),
		prev: lenient(z.array(z.string().min(1).max(200)).max(100)),
	});
}
